import { PolicyType } from './policy';
const { LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT, REMINDER, SMS, STATEMENT, ESCALATION, CONTACT_ESCALATION, QUOTE_EMAIL, PAYMENT_PLAN_REMINDER, ON_DEMAND_PLACEHOLDER, } = PolicyType;
const allTypes = Object.values(PolicyType);
/** Types that run per invoice and go through the common invoice checks (thresholds, due date window, branding themes) */
const invoiceBased = [LATE_FEE, DISCOUNT, REMINDER, SMS, ESCALATION, CONTACT_ESCALATION, PAYMENT_PLAN_REMINDER];
/** Types that run per contact against the contact's balance */
const contactBased = [STATEMENT, STATEMENT_LATE_FEE];
/** Types that send an email */
const emailSending = [REMINDER, LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT, STATEMENT, QUOTE_EMAIL, PAYMENT_PLAN_REMINDER, ESCALATION, CONTACT_ESCALATION];
/** Types that create an invoice (or invoice line) in the accounting system */
const invoiceCreating = [LATE_FEE, STATEMENT_LATE_FEE];
/** Types scheduled as an offset in days from a target date (as opposed to a day of the month) */
const dayOffsetScheduled = [LATE_FEE, DISCOUNT, REMINDER, SMS, ESCALATION, CONTACT_ESCALATION, QUOTE_EMAIL, PAYMENT_PLAN_REMINDER];
/** Every configurable type (excludes the hidden on-demand placeholder) */
const configurable = allTypes.filter(type => type !== ON_DEMAND_PLACEHOLDER);
const columnDefs = {
    id: {
        label: `ID`,
        description: `Primary key of the policy. Actions, groups and escalations reference it.`,
        usedBy: `all`,
    },
    organisation_id: {
        label: `Organisation`,
        description: `The organisation that owns the policy. Every policy query is scoped by it.`,
        usedBy: `all`,
    },
    title: {
        label: `Name`,
        description: `Display name shown in the policy list, action timeline and skip reasons.`,
        usedBy: `all`,
    },
    state: {
        label: `Status`,
        description: `Whether the policy runs automatically (active), is paused (inactive), or creates actions that must be approved before they run (approval). Ignored for system policies.`,
        usedBy: `all`,
    },
    calculation_type: {
        label: `Calculation`,
        description: `How the amount is calculated: fixed (uses fixed_fee_amount), percent (flat percent of the amount due), prorata (annual percent accrued daily) or not_applicable (fixed amount only, no interest).`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT],
        overrides: {
            [DISCOUNT]: `How the discount is calculated: fixed (uses fixed_fee_amount) or percent (percent of the invoice total). Prorata is not supported for discounts.`,
        },
    },
    process_type: {
        label: `Issue as`,
        description: `Whether the late fee is issued as a brand new invoice (new_invoice) or added as a line item on the overdue invoice (update_invoice). Statement interest is always issued as a new invoice.`,
        usedBy: [LATE_FEE],
    },
    fixed_fee_amount: {
        label: `Fixed fee`,
        labelOverrides: {
            [STATEMENT_LATE_FEE]: `Fixed charge`,
            [DISCOUNT]: `Fixed discount`,
        },
        description: `Flat amount in cents used when calculation_type is fixed.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT],
        overrides: {
            [STATEMENT_LATE_FEE]: `Flat fixed charge in cents added as its own line on top of the interest each period. See fixed_amount_line_description, fixed_amount_account_code and fixed_amount_tax_rate.`,
            [DISCOUNT]: `Flat discount amount in cents used when calculation_type is fixed.`,
        },
    },
    percent: {
        label: `Percent`,
        labelOverrides: {
            [STATEMENT_LATE_FEE]: `Annual interest rate`,
            [DISCOUNT]: `Discount percent`,
        },
        description: `Percentage stored as a whole number (5 = 5%). With calculation_type percent it is a one-off flat percent of the amount due; with prorata it is an annual rate divided by 365 to accrue daily.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT],
        overrides: {
            [DISCOUNT]: `Percentage discount off the invoice total, stored as a whole number (5 = 5%). Always a flat one-off percent.`,
        },
    },
    grace_period: {
        label: `Grace period (days)`,
        labelOverrides: {
            [DISCOUNT]: `Remove discount (days before)`,
        },
        description: `Number of days after the target date (due or issue date, see target_status) before the late fee first applies.`,
        usedBy: [LATE_FEE, DISCOUNT],
        overrides: {
            [DISCOUNT]: `Number of days before the removal anchor date (see discount_removal_target_status) that the discount is removed if the invoice is still unpaid.`,
        },
    },
    invoice_status: {
        label: `Invoice status`,
        description: `Accounting system status (e.g. AUTHORISED or DRAFT) the generated invoice is saved as.`,
        usedBy: invoiceCreating,
    },
    repeating: {
        label: `Repeat fee`,
        description: `Whether the late fee can be charged again on the same invoice after the first fee. System policies are always treated as repeating.`,
        usedBy: [LATE_FEE],
    },
    repeat_frequency: {
        label: `Repeat every (days)`,
        description: `Number of days between repeat late fees on the same invoice when repeating is on. Also the prorata accrual window for repeat fees.`,
        usedBy: [LATE_FEE],
    },
    use_contact_tax_rate: {
        label: `Use contact tax rate`,
        description: `Use the contact's default sales tax rate on the generated line item instead of the policy tax_rate. Xero only.`,
        usedBy: [LATE_FEE, DISCOUNT],
    },
    line_item_text: {
        label: `Line item description`,
        description: `Description on the generated late fee line item. Supports placeholders such as #PenaltyDate and #OriginalInvoiceNumber.`,
        usedBy: [LATE_FEE, DISCOUNT],
        overrides: {
            [DISCOUNT]: `Description on the discount line item. Supports #DueDate, #DiscountDate, #DiscountAmount and #OriginalTotal placeholders.`,
        },
    },
    deleted: {
        label: `Deleted`,
        description: `Soft delete flag. Deleted policies are hidden and never run.`,
        usedBy: `all`,
    },
    minimum_threshold: {
        label: `Minimum due amount`,
        labelOverrides: {
            [STATEMENT]: `Minimum balance`,
            [STATEMENT_LATE_FEE]: `Minimum overdue balance`,
        },
        description: `Minimum invoice amount due in cents for the policy to apply. 0 means no minimum.`,
        usedBy: [...invoiceBased, ...contactBased],
        overrides: {
            [STATEMENT]: `Minimum contact balance in cents (overdue or outstanding depending on statement_type) for a statement to be sent. 0 means no minimum.`,
            [STATEMENT_LATE_FEE]: `Minimum contact overdue balance in cents for interest to be charged. 0 means no minimum.`,
        },
    },
    maximum_threshold: {
        label: `Maximum due amount`,
        labelOverrides: {
            [STATEMENT]: `Maximum balance`,
            [STATEMENT_LATE_FEE]: `Maximum overdue balance`,
        },
        description: `Maximum invoice amount due in cents for the policy to apply. 0 means no maximum.`,
        usedBy: [...invoiceBased, ...contactBased],
        overrides: {
            [STATEMENT]: `Maximum contact balance in cents for a statement to be sent. 0 means no maximum.`,
            [STATEMENT_LATE_FEE]: `Maximum contact overdue balance in cents for interest to be charged. 0 means no maximum.`,
        },
    },
    apply_after_due_date_string: {
        label: `Apply only to invoices due from`,
        description: `Only apply to invoices due on or after this date (YYYY-MM-DD). Skipped for system policies.`,
        usedBy: invoiceBased,
    },
    apply_before_due_date_string: {
        label: `Apply only to invoices due before`,
        description: `Only apply to invoices due before this date (YYYY-MM-DD). Skipped for system policies.`,
        usedBy: invoiceBased,
    },
    policy_type: {
        label: `Policy type`,
        description: `Discriminator that selects which behaviour runs for the policy.`,
        usedBy: `all`,
    },
    discount_applies: {
        label: `Discount applies to`,
        description: `Which invoices get the discount. "unsent" restricts it to invoices not yet sent to the customer; "new" applies to all new invoices.`,
        usedBy: [DISCOUNT],
    },
    xero_account_code: {
        label: `Sales account`,
        description: `Accounting system account code for the generated fee, interest or discount line item.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT],
    },
    template_id: {
        label: `Email template`,
        description: `Email template used for the email this policy sends.`,
        usedBy: emailSending.filter(type => type !== ESCALATION && type !== CONTACT_ESCALATION),
        overrides: {
            [LATE_FEE]: `Email template used to notify the customer about the late fee. When null no email is sent for the fee.`,
            [STATEMENT_LATE_FEE]: `Email template used to notify the customer about the statement interest invoice. When null no email is sent.`,
            [DISCOUNT]: `Email template used to tell the customer a prompt payment discount was applied.`,
        },
    },
    ai_prompt: {
        label: `Intelligent Dynamic prompt`,
        description: `When set, the reminder ignores template_id and AI writes each email from this prompt plus the invoice and customer context. Needs the INTELLIGENT_DYNAMIC_EMAILS feature flag. Not for paid (MARKED_PAID) reminders.`,
        usedBy: [REMINDER],
    },
    reminder_platform: {
        label: `Channel`,
        description: `Channel the reminder is sent on. Always "email"; stored but not branched on by the backend.`,
        usedBy: [REMINDER],
    },
    day_index: {
        label: `Days from target date`,
        labelOverrides: {
            [STATEMENT]: `Day of month / weekday`,
            [STATEMENT_LATE_FEE]: `Day of month / weekday`,
            [CONTACT_ESCALATION]: `Days after oldest due date`,
            [QUOTE_EMAIL]: `Days from quote date`,
            [PAYMENT_PLAN_REMINDER]: `Days from installment date`,
        },
        description: `Number of days relative to the target_status date to run on. Negative means before, positive after. Counted in calendar or business days per day_period_type.`,
        usedBy: [...dayOffsetScheduled.filter(type => type !== LATE_FEE), ...contactBased],
        overrides: {
            [DISCOUNT]: `Number of days after the target_status date (issue or due) the discount is applied to the invoice.`,
            [CONTACT_ESCALATION]: `Number of days after the contact's oldest overdue invoice due date to raise the escalation.`,
            [QUOTE_EMAIL]: `Number of days relative to the quote expiry date or issue date (see target_status) to send. Negative means before.`,
            [PAYMENT_PLAN_REMINDER]: `Number of days relative to the installment due date or plan creation date (see target_status) to send. Negative means before.`,
            [STATEMENT]: `Day of the month (1-31, clamped to month length) when interval_type is monthly, or weekday number (1 = Monday, 7 = Sunday) when weekly.`,
            [STATEMENT_LATE_FEE]: `Day of the month (1-31, clamped to month length) to charge interest when interval_type is monthly, or weekday number (1 = Monday, 7 = Sunday) when weekly.`,
        },
    },
    target_status: {
        label: `Target date`,
        labelOverrides: {
            [LATE_FEE]: `Count grace period from`,
            [DISCOUNT]: `Apply discount from`,
            [QUOTE_EMAIL]: `Quote date`,
            [PAYMENT_PLAN_REMINDER]: `Payment plan date`,
        },
        description: `Which date day_index is offset from: due (invoice due date), issued (invoice issue date), marked_paid (payment date), invoice_amount_updated, promise_broken (a payment promise was broken) or promise_due (the promised payment date).`,
        usedBy: dayOffsetScheduled,
        overrides: {
            [LATE_FEE]: `Which date grace_period is counted from: due (default) or issued.`,
            [DISCOUNT]: `Which date day_index is counted from when applying the discount: issued (default) or due.`,
            [CONTACT_ESCALATION]: `Always oldest_due: the due date of the contact's oldest overdue invoice.`,
            [QUOTE_EMAIL]: `Which quote date day_index is offset from: expiry_date or quote_issue_date.`,
            [PAYMENT_PLAN_REMINDER]: `Which payment plan date day_index is offset from: instalment_due_date or payment_plan_created.`,
        },
    },
    discount_removal_target_status: {
        label: `Remove discount relative to`,
        description: `Anchor for the discount removal date: due or issued. The discount is removed grace_period days before this date.`,
        usedBy: [DISCOUNT],
    },
    include_invoice_pdf: {
        label: `Attach invoice PDF`,
        description: `Attach the invoice PDF to the email.`,
        usedBy: [REMINDER, PAYMENT_PLAN_REMINDER],
    },
    sms_content: {
        label: `SMS message`,
        description: `Mustache template rendered into the SMS body, e.g. {{{InvoiceNumber}}}, {{{DueDate}}}.`,
        usedBy: [SMS],
    },
    attach_ics: {
        label: `Attach calendar reminder`,
        description: `Attach a calendar (.ics) reminder for the due date to the email.`,
        usedBy: [REMINDER, PAYMENT_PLAN_REMINDER],
    },
    exclude_branding_themes: {
        label: `Exclude branding themes`,
        description: `Comma separated Xero branding theme IDs. Invoices with one of these themes are skipped. Xero only.`,
        usedBy: [...invoiceBased, STATEMENT],
        overrides: {
            [STATEMENT]: `Comma separated Xero branding theme IDs. The statement is skipped if the contact has any outstanding invoice with one of these themes. Xero only.`,
        },
    },
    on_update_fail_send_new: {
        label: `Create new invoice if update fails`,
        description: `When process_type is update_invoice and updating the existing invoice fails, fall back to creating a new late fee invoice.`,
        usedBy: [LATE_FEE],
    },
    updated_at: {
        label: `Updated at`,
        description: `Timestamp of the last update to the policy row.`,
        usedBy: `all`,
    },
    created_at: {
        label: `Created at`,
        description: `Timestamp the policy row was created.`,
        usedBy: `all`,
    },
    version: {
        label: `Version`,
        description: `Counter incremented on every update. Audit only, not used for locking.`,
        usedBy: `all`,
    },
    interval_type: {
        label: `Frequency`,
        description: `How often the policy runs and how day_index is read: monthly (day of month), weekly (weekday) or monthly_weekdays (day of month, moved to the next business day when it falls on a weekend or public holiday).`,
        usedBy: contactBased,
    },
    overdue_age_min: {
        label: `Minimum days overdue`,
        description: `Only include invoices overdue by at least this many days in the interest calculation.`,
        usedBy: contactBased,
        overrides: {
            [STATEMENT]: `When statement_type is overdue, only send if the contact has an invoice overdue by at least this many days.`,
        },
    },
    overdue_age_max: {
        label: `Maximum days overdue`,
        description: `Only include invoices overdue by at most this many days in the interest calculation. Null means no upper bound.`,
        usedBy: contactBased,
        overrides: {
            [STATEMENT]: `When statement_type is overdue, only send if the contact has an invoice overdue by at most this many days. Null means no upper bound.`,
        },
    },
    amounts_tax_line_type: {
        label: `Line amounts are`,
        description: `Line amount type on the generated interest invoice: tax inclusive, tax exclusive or no tax.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    adjust_for_credit: {
        label: `Adjust for credit`,
        description: `Reduce the balance interest is charged on by the contact's unallocated credit notes, prepayments and overpayments.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    tax_rate: {
        label: `Tax rate`,
        description: `Accounting system tax rate applied to the generated fee, interest or discount line item.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT],
    },
    penalty_due_date: {
        label: `Fee invoice due in (days)`,
        description: `Number of days from the date the fee invoice is created until it is due. 0 means due immediately.`,
        usedBy: invoiceCreating,
    },
    statement_type: {
        label: `Balance type`,
        labelOverrides: {
            [STATEMENT]: `Statement type`,
            [STATEMENT_LATE_FEE]: `Charge interest on`,
        },
        description: `Which balance the policy looks at: outstanding, overdue, all or in_credit.`,
        usedBy: contactBased,
        overrides: {
            [STATEMENT]: `Which contacts qualify and which balance is checked: outstanding (any balance), overdue (only overdue balances), all, or in_credit.`,
            [STATEMENT_LATE_FEE]: `overdue (default) charges interest only on overdue invoices; outstanding charges interest on the whole outstanding balance.`,
        },
    },
    day_period_type: {
        label: `Count days as`,
        description: `Whether day_index and grace_period count calendar days or business days, and whether a missed run date is caught up later (the "or later" variants).`,
        usedBy: [...dayOffsetScheduled, STATEMENT_LATE_FEE],
    },
    escalation_type: {
        label: `Escalation type`,
        description: `Type of escalation task created: call, stop_credit, letter or other.`,
        usedBy: [ESCALATION, CONTACT_ESCALATION],
    },
    escalation_steps: {
        label: `Escalation instructions`,
        description: `Mustache templated instructions rendered into the escalation task content.`,
        usedBy: [ESCALATION, CONTACT_ESCALATION],
    },
    escalation_emails: {
        label: `Notify emails`,
        description: `Comma separated internal email addresses notified when the escalation is raised.`,
        usedBy: [ESCALATION, CONTACT_ESCALATION],
    },
    statement_period: {
        label: `Statement period`,
        description: `Relative period of account activity the statement covers, e.g. all time, this month, last month.`,
        usedBy: [STATEMENT],
    },
    include_attachments: {
        label: `Attach invoice attachments`,
        description: `Attach the invoice's accounting system attachments to the email.`,
        usedBy: [REMINDER, PAYMENT_PLAN_REMINDER],
    },
    min_fee_amount: {
        label: `Minimum charge`,
        description: `Minimum charge in cents. If interest plus fixed charge is below this, a "Minimum Charge Adjustment" line tops it up.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    exclude_account_lines: {
        label: `Exclude/include invoice lines`,
        description: `Comma separated account codes whose invoice lines are excluded from the amount the policy calculates on. Prefix with INCLUDE_ONLY: to instead only include those codes.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT, REMINDER, SMS, ESCALATION],
    },
    use_central_interest_rate: {
        label: `Add central bank rate`,
        description: `Add the region's central bank interest rate to percent when calculating prorata interest.`,
        usedBy: invoiceCreating,
    },
    invoice_reference: {
        label: `Invoice reference`,
        description: `Reference template for the generated invoice, e.g. "Penalty for {{{OriginalInvoiceNumber}}}".`,
        usedBy: invoiceCreating,
        overrides: {
            [STATEMENT_LATE_FEE]: `Reference template for the generated interest invoice, e.g. "Overdue account charges as on {{{IssueDate}}}".`,
        },
    },
    system: {
        label: `System policy`,
        description: `Hidden system policy created automatically by Paidnice for ad hoc and default actions. Skips group checks, approval and scheduling delays.`,
        usedBy: `all`,
    },
    hour_of_day: {
        label: `Time of day (hour)`,
        description: `Hour (0-23) in the organisation's local time the action is scheduled for.`,
        usedBy: configurable,
    },
    use_invoice_number_prefix: {
        label: `Use Paidnice invoice numbering`,
        description: `Number the generated invoice with Paidnice's own prefixed sequence instead of the accounting system default. Not Xero.`,
        usedBy: invoiceCreating,
    },
    include_previous_interest: {
        label: `Include previous interest`,
        description: `Include previously issued Paidnice interest invoices in the balance interest is charged on.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    reference_contains: {
        label: `Reference contains`,
        description: `Only apply to invoices whose reference contains this text. Use re:/.../ for a regex.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE, DISCOUNT, REMINDER, SMS, ESCALATION],
        overrides: {
            [STATEMENT_LATE_FEE]: `Only include invoices whose reference contains this text in the interest calculation. Use re:/.../ for a regex.`,
        },
    },
    only_webhook: {
        label: `Webhook only`,
        description: `Do not send the SMS. Only fire the outbound webhook with the message content.`,
        usedBy: [SMS],
    },
    destination_email_override: {
        label: `Send all emails to`,
        description: `Send every email from this policy to this address instead of the contact's email.`,
        usedBy: [STATEMENT, REMINDER, QUOTE_EMAIL, PAYMENT_PLAN_REMINDER],
    },
    new_invoice_branding_theme_id: {
        label: `Branding theme for new invoice`,
        description: `Xero branding theme applied to the generated invoice. Xero only.`,
        usedBy: invoiceCreating,
    },
    parent_only: {
        label: `Parent contacts only`,
        description: `Only apply to parent contacts; child contacts are skipped and rolled into the parent.`,
        usedBy: [...contactBased, CONTACT_ESCALATION],
    },
    statement_interest_to: {
        label: `Charge interest up to`,
        description: `End of the interest period: default (the run date) or end_of_previous_month.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    sms_fallback_to_phone: {
        label: `Fall back to phone number`,
        description: `Fall back to the contact's default phone number when no mobile number is set.`,
        usedBy: [SMS],
    },
    sms_default_country_code: {
        label: `Default country code`,
        description: `Default country code used when parsing phone numbers without one.`,
        usedBy: [SMS],
        overrides: {},
    },
    tracking_category_class: {
        label: `Tracking category / class`,
        description: `Xero tracking category option (stored as "categoryId:optionId") or QBO class applied to the generated line item.`,
        usedBy: invoiceCreating,
    },
    contact_tags: {
        label: `Contact tags`,
        description: `Comma separated contact tags. Combined with contact_tags_exclude_include to include or exclude contacts with any of these tags.`,
        usedBy: configurable,
    },
    contact_tags_exclude_include: {
        label: `Contact tags mode`,
        description: `Whether contact_tags is an include list (only contacts with a tag) or an exclude list (skip contacts with a tag).`,
        usedBy: configurable,
    },
    contact_address_filter_field: {
        label: `Address filter field`,
        description: `Contact address field to filter on: country, state or city.`,
        usedBy: configurable,
    },
    contact_address_filter_operator: {
        label: `Address filter operator`,
        description: `Operator for the address filter: is, is_not or contains.`,
        usedBy: configurable,
    },
    contact_address_filter_value: {
        label: `Address filter value`,
        description: `Value the contact address field is compared against.`,
        usedBy: configurable,
    },
    xero_group_name: {
        label: `Xero contact groups`,
        description: `Comma separated Xero contact group names. Combined with xero_group_name_exclude_include to include or exclude contacts in these groups. Xero only.`,
        usedBy: configurable,
    },
    xero_group_name_exclude_include: {
        label: `Xero contact groups mode`,
        description: `Whether xero_group_name is an include list or an exclude list.`,
        usedBy: configurable,
    },
    do_not_send_email_to_customer: {
        label: `Do not email customer`,
        description: `Create the late fee but do not email the customer about it.`,
        usedBy: [LATE_FEE],
    },
    first_time_only: {
        label: `First late fee only`,
        description: `Only apply if the invoice has never had a completed late fee from any policy.`,
        usedBy: [LATE_FEE],
    },
    assignee_user_id: {
        label: `Assign to`,
        description: `User the created escalation task is assigned to by default.`,
        usedBy: [ESCALATION, CONTACT_ESCALATION],
    },
    assign_to_record_assignee: {
        label: `Assign to invoice/contact assignee`,
        description: `Assign the created escalation task to the invoice assignee, falling back to the contact assignee. Overrides assignee_user_id. Left unassigned if neither has one.`,
        usedBy: [ESCALATION, CONTACT_ESCALATION],
    },
    include_statement_pdf: {
        label: `Attach statement PDF`,
        description: `Attach an outstanding balance statement PDF to the statement email.`,
        usedBy: [STATEMENT],
    },
    include_activity_pdf: {
        label: `Attach activity PDF`,
        description: `Attach an account activity statement PDF to the statement email.`,
        usedBy: [STATEMENT],
    },
    statement_template_id: {
        label: `Statement template`,
        description: `AI statement builder template used for the attached outstanding statement PDF. Null uses the organisation default.`,
        usedBy: [STATEMENT],
    },
    use_standard_statement: {
        label: `Use standard statement`,
        description: `When true, the attached outstanding statement PDF uses the standard template, ignoring the organisation default and statement_template_id.`,
        usedBy: [STATEMENT],
    },
    mark_as_sent: {
        label: `Mark invoice as sent`,
        description: `Mark the invoice as sent in the accounting system when the action completes.`,
        usedBy: [REMINDER, DISCOUNT],
    },
    accounting_system_location: {
        label: `Location`,
        description: `QBO location (department) set on the generated invoice. QBO only.`,
        usedBy: invoiceCreating,
    },
    earliest_interest_date_string: {
        label: `Earliest interest date`,
        description: `Floor date (YYYY-MM-DD) for prorata interest. Interest never accrues from before this date.`,
        usedBy: [LATE_FEE, STATEMENT_LATE_FEE],
    },
    only_send_to_primary_contact: {
        label: `Primary contact only`,
        description: `Only send to the contact's primary person instead of every person flagged for notifications.`,
        usedBy: [REMINDER, LATE_FEE, STATEMENT, PAYMENT_PLAN_REMINDER],
    },
    fixed_amount_line_description: {
        label: `Fixed charge description`,
        description: `Description of the fixed charge line item. Defaults to "Fixed Charge".`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    fixed_amount_account_code: {
        label: `Fixed charge account`,
        description: `Account code for the fixed charge line item. Falls back to xero_account_code.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    fixed_amount_tax_rate: {
        label: `Fixed charge tax rate`,
        description: `Tax rate for the fixed charge line item. Falls back to tax_rate.`,
        usedBy: [STATEMENT_LATE_FEE],
    },
    email_sender_profile_id: {
        label: `Send from`,
        description: `Email sender profile (from address and name) to send from. Null uses the organisation default.`,
        usedBy: emailSending,
    },
    webhook_disabled: {
        label: `Disable webhook`,
        description: `Suppress the organisation's outbound webhook for actions created by this policy.`,
        usedBy: configurable,
    },
    skip_when_payment_promised: {
        label: `Skip when payment promised`,
        description: `When true (default), skip this policy while the invoice has an open payment promise and the policy would apply before the promised date. Ignored by promise_due policies.`,
        usedBy: [REMINDER, SMS, ESCALATION, LATE_FEE, DISCOUNT],
    },
    skip_when_query_open: {
        label: `Skip while a query is open`,
        description: `When true (default), skip this policy while the invoice has an open query.`,
        usedBy: [REMINDER, SMS, ESCALATION, LATE_FEE, DISCOUNT],
    },
};
function buildDescriptions() {
    var _a, _b, _c, _d;
    const result = {};
    for (const type of allTypes) {
        const columns = {};
        for (const column of Object.keys(columnDefs)) {
            const def = columnDefs[column];
            columns[column] = {
                used: def.usedBy === `all` || def.usedBy.includes(type),
                label: (_b = (_a = def.labelOverrides) === null || _a === void 0 ? void 0 : _a[type]) !== null && _b !== void 0 ? _b : def.label,
                description: (_d = (_c = def.overrides) === null || _c === void 0 ? void 0 : _c[type]) !== null && _d !== void 0 ? _d : def.description,
            };
        }
        result[type] = columns;
    }
    return result;
}
export const policyColumnDescriptions = buildDescriptions();
//# sourceMappingURL=policyColumnDescriptions.js.map